// A big, dismissable QR code of the current route's share link, so someone can
// scan it off your screen to open the exact same route on their phone. Opened
// from the route pill's ⌃ menu ("QR Code").
//
// ⚠️ THE CODE IS DRAWN BY THE HOST, through the `renderQr` render prop. Copy-out
// clean means no imports from outside src/pathfinder, and the UMaps mark that
// belongs in the middle of every code this product makes is a host asset — the
// same rule `appPromo` follows, and for the same reason: whose brand this is,
// is not the pathfinder's business.
//
// Without the prop this falls back to a public generator service, so a bare
// copy of this folder still shows a code. That fallback is a DEGRADED one and
// is not what myumaps.ca serves: the service defaults to error-correction level
// L, which has no budget for a logo, and it cannot carry the mark at all. The
// link it encodes is a plain, already-shareable route URL (no personal data),
// which is the only reason handing it to a third party is acceptable even as a
// fallback.

import { useState } from 'react';
import { createPortal } from 'react-dom';

export default function QrPopup({ url, onClose, renderQr = null }) {
  const [copied, setCopied] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);

  const link = url || window.location.href;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=320x320&margin=12&data=${encodeURIComponent(link)}`;

  const copy = async () => {
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(link);
      else {
        const ta = document.createElement('textarea');
        ta.value = link; ta.setAttribute('readonly', '');
        ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select();
        document.execCommand('copy'); document.body.removeChild(ta);
      }
    } catch { /* still show confirmation */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Portalled to <body>. This is opened from inside the bottom route pill, and
  // that pill carries a z-index, so it opens a stacking context: without the
  // portal this overlay's z-index is resolved *within* the pill and the map's
  // floating panes (the open panorama window) paint straight over a modal that
  // is supposed to sit above everything. `position: fixed` alone doesn't help —
  // it escapes the pill's positioning, not its stacking context.
  return createPortal(
    <div className="pfa-fb-backdrop pfa-qr-overlay" onClick={onClose}>
      <div className="pfa-fb-card pfa-qr-card" onClick={e => e.stopPropagation()}>
        <button className="pfa-fb-close" onClick={onClose} aria-label="Dismiss">✕</button>
        <div className="pfa-fb-title">Scan to open this route</div>
        <div className="pfa-fb-sub">Point a phone camera at the code to open these directions.</div>
        {renderQr ? (
          <div className="pfa-qr-imgwrap">{renderQr({ url: link, size: 320 })}</div>
        ) : imgFailed ? (
          <div className="pfa-qr-fallback">Couldn’t load the QR code — copy the link instead.</div>
        ) : (
          <div className="pfa-qr-imgwrap">
            <img
              className="pfa-qr-img"
              src={qrSrc}
              alt="QR code linking to this route"
              width={320}
              height={320}
              onError={() => setImgFailed(true)}
            />
          </div>
        )}
        <button className="pfa-qr-copy" onClick={copy}>
          {copied ? '✓ Link copied' : '🔗 Copy link instead'}
        </button>
      </div>
    </div>,
    document.body
  );
}
